<template>
  <div class="finance_box" ref="myElementRef">
    <div ref="searchBox">
      <hc-search :data="searchData" @serch="getSearch" />
    </div>
    <div class="table_box">
      <hc-table :loading="state.loading" :tableData="state.tableData" :column="state.tableHeader" :offsetHeight="state.offsetHeight" :maxPage="maxPage"
        @pageChange="pageChange" @limitChange="limitChange">
        <template #leave_type="{ row }">{{ row.leave_type === 'annual_leave' ? t('attendance.annualLeave') : t('attendance.leave') }}</template>
        <template #status="{ row }">
          <el-tag :type="statusTypeMap[row.status] || 'info'" size="small">{{ statusLabelMap[row.status] || '-' }}</el-tag>
        </template>
        <template #operation="{ row }">
          <el-button v-if="canAudit(row)" class="opBtn" size="small" type="primary" link @click="openAudit(row)">{{ t('attendance.audit') }}</el-button>
        </template>
      </hc-table>
    </div>
    <AnnualLeaveAudit ref="auditRef" @refresh="getSearch(searchData)" />
  </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import AnnualLeaveAudit from "./components/annualLeaveAudit.vue";
import { reactive, ref, nextTick, onMounted, onUnmounted, toRaw } from "vue";
import { listToMap } from "@/utils/utils";
import { pageListApi } from "@/api/attendance/annualLeave";
import { isTenantAdminRole } from "@/utils/adminMenuPermission.js";
import { useAttendancePageI18n, t } from '@/composables/useAttendancePageI18n.js';

const myElementRef = ref();
const searchBox = ref();
const auditRef = ref();
const page = ref(1);
const limit = ref(20);
const maxPage = ref(0);

const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
const currentUser = userInfo?.users || userInfo?.user?.users || userInfo || {};
const roleId = Number(currentUser?.roleId || currentUser?.role_id || 0);

const buildStatusMap = () => ({
  0: t('attendance.pendingAudit'),
  1: t('attendance.captainApproved'),
  2: t('attendance.captainRejected'),
  3: t('attendance.adminApproved'),
  4: t('attendance.adminRejected'),
});

const statusLabelMap = reactive(buildStatusMap());
const statusTypeMap = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'success', 4: 'danger' };

const buildSearchStatusOptions = () => ([
  { label: t('attendance.pendingAudit'), value: 0 },
  { label: t('attendance.captainApproved'), value: 1 },
  { label: t('attendance.captainRejected'), value: 2 },
  { label: t('attendance.adminApproved'), value: 3 },
  { label: t('attendance.adminRejected'), value: 4 },
  { label: t('common.all'), value: 99 },
]);

const buildLeaveTypeOptions = () => ([
  { label: t('common.all'), value: '' },
  { label: t('attendance.leave'), value: 'leave' },
  { label: t('attendance.annualLeave'), value: 'annual_leave' },
]);

const searchStatusOptions = reactive(buildSearchStatusOptions());
const leaveTypeOptions = reactive(buildLeaveTypeOptions());

const searchData = reactive([
  { label: "", type: "select", value: 99, field: 'status', clearable: false, children: searchStatusOptions },
  { label: "", type: "select", value: '', field: 'leave_type', children: leaveTypeOptions },
]);

const buildColumns = () => ([
  { show: true, type: "index", label: t('table.index'), width: 80 },
  { prop: "name", show: true, label: t('field.realName') },
  { prop: "leave_type", show: true, label: t('attendance.leaveType') },
  { prop: "start_date", show: true, label: t('attendance.startDate') },
  { prop: "end_date", show: true, label: t('attendance.endDate') },
  { prop: "leave_days", show: true, label: t('attendance.days') },
  { prop: "reason", show: true, label: t('attendance.reason') },
  { prop: "status", show: true, label: t('field.status') },
  { prop: "created_at", show: true, label: t('attendance.createdAt') },
  { prop: "operation", show: true, label: t('table.operation') },
]);

const applyLabels = () => {
  searchData[0].label = t('field.status');
  searchData[1].label = t('attendance.leaveType');
  Object.assign(searchStatusOptions, buildSearchStatusOptions());
  Object.assign(leaveTypeOptions, buildLeaveTypeOptions());
  Object.assign(statusLabelMap, buildStatusMap());
  state.column = buildColumns();
  tableHeaderFn();
};

useAttendancePageI18n(applyLabels);

const state = reactive({
	loading: false,
  offsetHeight: 0,
  tableHeader: [],
  tableData: [],
  column: buildColumns(),
});

const canAudit = (row) => {
  const s = Number(row.status);
  if (roleId === 2) return s === 0;
  if (isTenantAdminRole(roleId)) return s === 0 || s === 1;
  return false;
}
const openAudit = (row) => auditRef.value.open(row);
const pageChange = (p) => { page.value = p; getSearch(searchData); }
const limitChange = (l) => { limit.value = l; getSearch(searchData); }
const tableHeaderFn = () => state.tableHeader = state.column.filter(v => v.show);

const offHeighths = () => {
  if (myElementRef.value) state.offsetHeight = myElementRef.value?.offsetHeight - 90 - searchBox.value?.offsetHeight;
}

const getSearch = (params) => {
  params.forEach(i => i.value = i.value ?? '');
  const form = listToMap(toRaw(params), { value: 'value', key: 'field' });
  state.loading = true
  pageListApi(JSON.stringify({ page: page.value, limit: limit.value, ...form })).then(res => {
    maxPage.value = res?.maxPage ?? 0;
    state.tableData = res?.data ?? [];
  }).finally(() => { state.loading = false })
}

onMounted(() => {
  applyLabels();
  nextTick(() => offHeighths());
  window.onresize = () => nextTick(() => offHeighths());
  tableHeaderFn();
  getSearch(searchData);
});
onUnmounted(() => { window.onresize = null; });
</script>

<style lang="less" scoped>
.finance_box { flex: 1; overflow: auto; background-color: #fff; border-radius: 6px; padding: 10px; margin-right: 10px; }
.table_box { width: 100%; }
.opBtn { margin: 0 10px 0 0; }
</style>
